import { NavbarItem } from '@edassist-ui/navbar';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { combineLatest, of, Subscription } from 'rxjs';
import {
	AuthenticationService,
	NotificationAccess,
	NotificationService,
	PermissionService,
	ContentOptions,
	ContentService,
	ActiveProductService,
	Product
} from '@edassist/api';
import { SliderService } from '@edassist-ui/services';
import {
	AppConfigService,
	CLIENT_FEATURE_REFERENCE_CODES,
	constants,
	FEATURE_FLAGS,
	HybridAppService,
	LocalStorageService,
	NotificationAlertIndicatorService,
	ParticipantUtilityService,
	SiteMapLinkService,
	WINDOW,
	GeneralContentService,
	UserTypeService,
	ClientService,
	GoogleTagManagerEventService
} from '@edassist/common';
import { filter, map, switchMap } from 'rxjs/operators';
import { CareerPathwaysTabsService } from '@internal-admin/app/modules/career-pathways/career-pathways-tabs.service';
import { ClientSetupSettings } from '@internal-admin/app/modules/client-view/career-pathway-client-setup/client-setup-settings/client-setup-settings.interface';

@Component({
	selector: 'tu-navbar',
	templateUrl: './tuition-navbar.component.html',
	styleUrls: ['./tuition-navbar.component.scss']
})
export class TuitionNavbarComponent implements OnInit, OnDestroy {
	clientSetupSettings: ClientSetupSettings;
	isLoggedIn: boolean;
	navbarItems: Array<NavbarItem> = [];
	constants = constants;
	isNewNavigationBarEnabled = false;
	participantId: number;
	isSiteRedesignFeatureEnabled: boolean;
	selfScheduledAdvising: boolean;
	notificationsAriaLabel: string;
	showNavItems = true;
	unreadCountTotal = 0;
	weProductNameCharLimit = 30;
	weProductName = '';
	isNotificationsEnabled = false;
	isFranchiseOperator = false;
	isFranchiseOperatorProxy = false;
	activeProduct: number;
	showSupportServices = true;
	isCareerPathwayEnabled: boolean;
	private unreadNotificationCount = 0;
	private announcementAndAdminMsgCount = 0;
	showNotificationsTooltip = true;

	private readonly subscriptions = new Subscription();
	private notificationAccess: NotificationAccess;

	constructor(
		@Inject(WINDOW) protected readonly window: Window,
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
		protected readonly sliderService: SliderService,
		private readonly authService: AuthenticationService,
		private readonly permissionService: PermissionService,
		private readonly hybridAppService: HybridAppService,
		protected readonly clientService: ClientService,
		protected readonly siteMapLinkService: SiteMapLinkService,
		private readonly notificationAlertIndicatorService: NotificationAlertIndicatorService,
		private readonly notificationService: NotificationService,
		private readonly contentService: ContentService,
		private readonly gtmEventService: GoogleTagManagerEventService,
		private readonly activeProductService: ActiveProductService,
		private readonly careerPathwaysService: CareerPathwaysTabsService
	) {
		this.isNotificationsEnabled = AppConfigService.isFeatureFlagEnabled(FEATURE_FLAGS.NOTIFICATIONS_ENABLED);
		if (this.isNotificationsEnabled) {
			this.subscriptions.add(
				siteMapLinkService.notifications.subscribe(() => {
					this.openNotifications();
				})
			);
		}
	}

	ngOnInit(): void {
		const clientLoginDetails = ParticipantUtilityService.getClientLoginDetails();
		const clientId = LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS)?.code;
		const newBenefitsAndSupportPage = AppConfigService.isNewBenefitsAndSupportPageEnabled(Number(clientId));
		const contentOptionsClient: ContentOptions = {
			client: clientLoginDetails?.clientId.toString(),
			textOnly: false
		};
		this.isCareerPathwayEnabled = this.clientService.isClientFeatureEnabled(
			CLIENT_FEATURE_REFERENCE_CODES.CAREER_PATHWAYS
		);
		this.isNewNavigationBarEnabled = AppConfigService.isFeatureFlagEnabled(FEATURE_FLAGS.NEW_NAVIGATION_BAR_ENABLED);
		this.participantId = ParticipantUtilityService.getParticipantId();
		this.selfScheduledAdvising = clientLoginDetails.selfScheduledAdvising;
		if (this.isNotificationsEnabled) {
			this.subscriptions.add(
				this.notificationAlertIndicatorService.unreadNotificationCountEvent.subscribe((unreadNotificationCount) => {
					this.unreadNotificationCount = unreadNotificationCount;
					this.calculateUnreadCountForDisplay();
				})
			);
			this.subscriptions.add(
				this.notificationAlertIndicatorService.announcementAndAdminMsgCountEvent.subscribe(
					(announcementAndAdminMsgCount) => {
						this.announcementAndAdminMsgCount = announcementAndAdminMsgCount;
						this.calculateUnreadCountForDisplay();
						if (this.unreadCountTotal > 0) {
							this.notificationsAriaLabel = `${this.unreadCountTotal} ${this.unreadCountTotal === 1 ? 'Unread Notification' : 'Unread Notifications'}`;
						} else {
							this.notificationsAriaLabel = 'Notifications';
						}
					}
				)
			);
		}
		this.subscriptions.add(
			this.activeProductService.activeProduct.subscribe((product: Product) => {
				this.activeProduct = product?.id;
				if (this.activeProduct === constants.PRODUCTS.CLIENT_CUSTOM_PATHWAY.ID) {
					this.navbarItems = [];
					this.weProductName = product?.name;
					this.showSupportServices = false;
				} else {
					this.showSupportServices = true;
				}
			})
		);

		this.subscriptions.add(
			this.sliderService.closeSliderEvent.subscribe((sliderId) => {
				this.showNotificationsTooltip = sliderId === constants.sliderIds.NOTIFICATION;
			})
		);

		this.subscriptions.add(
			combineLatest([
				this.authService.currentUser,
				this.permissionService.userPermissions,
				this.isNotificationsEnabled ?
					this.notificationService.getNotificationAccess(this.participantId)
				:	of(undefined),
				this.careerPathwaysService.isCareerPathwaysEnabled(clientLoginDetails.clientId)
			]).subscribe(([user, actions, notificationAccess, isNewCareerPathwayEnabled]) => {
				this.isLoggedIn = user?.sessionToken?.length > 0;
				this.isFranchiseOperator = UserTypeService.isFranchiseOperator(user?.userTypeId);
				this.isFranchiseOperatorProxy = UserTypeService.isFranchiseOperatorProxy(user?.userTypeId);
				this.isSiteRedesignFeatureEnabled = this.clientService.isClientFeatureEnabled(
					CLIENT_FEATURE_REFERENCE_CODES.SITE_REDESIGN
				);

				if (notificationAccess) {
					this.notificationAccess = notificationAccess;
				} else {
					this.notificationAccess = {
						clientId: clientLoginDetails.clientId,
						participantId: this.participantId
					};
				}

				if (this.isNotificationsEnabled) {
					this.notificationAlertIndicatorService.notificationAccessed(this.notificationAccess);
				}
				if (this.isLoggedIn) {
					const homeNavbarItem: NavbarItem =
						this.isSiteRedesignFeatureEnabled ?
							new NavbarItem(
								`${this.constants.BASE_APP_ROUTES.HOME}`,
								this.constants.TUITION_PAGE_TITLES.HOME,
								this.isSiteRedesignFeatureEnabled,
								false
							)
						:	new NavbarItem(
								this.hybridAppService.generateHybridAppUrl(`${this.constants.BASE_APP_ROUTES.HOME}`),
								this.constants.TUITION_PAGE_TITLES.HOME,
								!this.isSiteRedesignFeatureEnabled,
								true
							);

					const coachingNavbarItem: NavbarItem =
						this.isSiteRedesignFeatureEnabled && this.selfScheduledAdvising && !this.isFranchiseOperator ?
							new NavbarItem(
								`${this.constants.BASE_APP_ROUTES.educationCoaching}`,
								this.constants.TUITION_PAGE_TITLES.EDUCATION_COACHING,
								this.selfScheduledAdvising,
								false
							)
						:	new NavbarItem(
								`${this.constants.BASE_APP_ROUTES.educationCoaching}`,
								this.constants.TUITION_PAGE_TITLES.LEARNING_RESOURCES,
								!this.selfScheduledAdvising,
								false
							);

					this.activeProduct = this.activeProductService.getActiveProduct()?.id;
					if (this.activeProduct !== constants.PRODUCTS.CLIENT_CUSTOM_PATHWAY.ID) {
						this.navbarItems = [
							homeNavbarItem,
							new NavbarItem(
								`${newBenefitsAndSupportPage ? this.constants.BASE_APP_ROUTES.benefitsAndSupport : this.constants.BASE_APP_ROUTES.benefitsSupport}`,
								this.constants.TUITION_PAGE_TITLES.BENEFITS_SUPPORT,
								this.isSiteRedesignFeatureEnabled,
								newBenefitsAndSupportPage ? false : true
							),
							coachingNavbarItem,
							new NavbarItem(
								`${this.constants.BASE_APP_ROUTES.careerPathways}`,
								this.constants.TUITION_PAGE_TITLES.CAREER_PATHWAYS,
								this.isSiteRedesignFeatureEnabled && isNewCareerPathwayEnabled,
								false
							),
							new NavbarItem(
								this.hybridAppService.generateHybridAppUrl(
									`${constants.BASE_APP_ROUTES.history}/${user.participantId}`
								),
								this.constants.TUITION_PAGE_TITLES.HISTORY,
								true,
								true
							),

							new NavbarItem(
								this.hybridAppService.generateHybridAppUrl(`${constants.BASE_APP_ROUTES.admin}`),
								this.constants.TUITION_PAGE_TITLES.ADMIN,
								user.userTypeId === this.constants.userType.clientAdmin,
								true
							),

							new NavbarItem(
								constants.BASE_APP_ROUTES.reviewApplications,
								this.constants.TUITION_PAGE_TITLES.REVIEW_APPLICATIONS,
								this.isSiteRedesignFeatureEnabled &&
									(user.userTypeId === this.constants.userType.clientAdmin ||
										user.userTypeId === this.constants.userType.supervisor ||
										this.isFranchiseOperatorProxy),
								false
							)
						];
					}

					if (this.isNotificationsEnabled) {
						this.calculateUnreadCountForDisplay();
					}
				}
			})
		);
		this.subscriptions.add(
			this.router.events
				.pipe(
					filter(
						(event) =>
							event instanceof NavigationEnd || (event instanceof Scroll && event.routerEvent instanceof NavigationEnd)
					),
					map(() => this.activatedRoute),
					map((route) => {
						let childRoute = route;
						while (childRoute.firstChild) {
							childRoute = childRoute.firstChild;
						}

						return childRoute;
					}),
					switchMap((route) => route?.data)
				)
				.subscribe((data) => {
					this.showNavItems = data?.hasOwnProperty('showNavBar') ? data.showNavBar : true;
				})
		);

		this.subscriptions.add(
			this.contentService
				.findByComponent(
					this.constants.contentConstants.contentTypes.general,
					this.constants.contentConstants.CONTENT_COMPONENTS.HOME,
					contentOptionsClient
				)
				.pipe(map((content) => GeneralContentService.mapContent(content)))
				.subscribe((content) => {
					if (this.activeProduct === constants.PRODUCTS.CLIENT_CUSTOM_PATHWAY.ID) {
						this.weProductName = content.productSelectionTile
							?.replace(/<\/?[^>]+(>|$)/g, '')
							.substring(0, this.weProductNameCharLimit);
					}

					if (content?.weProductName && this.activeProduct !== constants.PRODUCTS.CLIENT_CUSTOM_PATHWAY.ID) {
						this.weProductName = content.weProductName
							.replace(/<\/?[^>]+(>|$)/g, '')
							.substring(0, this.weProductNameCharLimit);
					}
				})
		);
	}

	openNotifications(): void {
		this.showNotificationsTooltip = false;
		this.sliderService.openSlider(constants.sliderIds.NOTIFICATION);

		this.notificationAccess.notificationLastReadDate = new Date().toISOString();
		this.notificationService
			.updateNotificationAccess(this.participantId, this.notificationAccess)
			.subscribe((notificationAccess) => {
				this.notificationAccess = notificationAccess;
				this.notificationAlertIndicatorService.notificationAccessed(this.notificationAccess);
				this.unreadNotificationCount = 0;
			});
	}

	handleKeydown(): void {
		document.getElementById('notificationHeader')?.focus();
	}

	navigateToFastTrackFlow(): void {
		const fastTrackFlowUrl = this.hybridAppService.generateHybridAppUrl(
			`${this.constants.BASE_APP_ROUTES.FAST_TRACK_BASE}${this.constants.BASE_APP_ROUTES.PROGRAMS_STEP}${this.participantId}`
		);
		// Send cta_click event to GTM
		this.gtmEventService.sendCtaClick(
			constants.googleAnalytics.callToAction.linkText.tuition,
			fastTrackFlowUrl,
			constants.googleAnalytics.callToAction.pageSection.header
		);
		this.window.location.href = fastTrackFlowUrl;
	}

	isActivePage(url: string): boolean {
		const currentUrl = this.router.url.replace(/^\//, '');

		return currentUrl === url;
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	private calculateUnreadCountForDisplay(): void {
		this.unreadCountTotal = this.unreadNotificationCount + this.announcementAndAdminMsgCount;
		LocalStorageService.setItem(
			constants.localStorageKeys.UNREAD_ANNOUNCEMENTS_ADMIN_REVIEW_COUNT,
			this.announcementAndAdminMsgCount
		);
	}
}
